// ─────────────────────────────────────────────────────────────────────────────
// i18n de Beartrack (react-i18next).
//
// Estrategia: CLAVES EN LENGUAJE NATURAL — la cadena en español ES la clave.
// Así se migra la app de forma incremental: basta envolver cualquier texto
// visible en t("Texto en español"); lo que no esté traducido cae a español
// (fallbackLng + returnKey). Idiomas: es (base), en, zh (chino simpl.), pt.
// ─────────────────────────────────────────────────────────────────────────────
import i18n from "i18next";
import { initReactI18next } from "react-i18next";

// Auto-carga y mergea TODOS los .json de cada carpeta de idioma. Así la
// migración se reparte en archivos por área (client.json, orders.json, …) que
// distintos colaboradores/olas pueden agregar sin pisarse. La clave (texto en
// español) es única, así que el merge plano es seguro.
function loadLocale(ctx) {
  return ctx.keys().reduce((acc, key) => Object.assign(acc, ctx(key)), {});
}
const en = loadLocale(require.context("./locales/en", false, /\.json$/));
const zh = loadLocale(require.context("./locales/zh", false, /\.json$/));
const pt = loadLocale(require.context("./locales/pt", false, /\.json$/));
const fr = loadLocale(require.context("./locales/fr", false, /\.json$/));
// Español: solo mensajes que el backend manda en inglés (las claves ya son español).
const es = loadLocale(require.context("./locales/es", false, /\.json$/));

export const LANGUAGES = [
  { code: "es", label: "Español" },
  { code: "en", label: "English" },
  { code: "zh", label: "简体中文" },
  { code: "pt", label: "Português" },
  { code: "fr", label: "Français" },
];

export const LANG_STORAGE_KEY = "beartrack.lang";

const SUPPORTED = LANGUAGES.map((l) => l.code);

// Idioma principal por defecto = el del dispositivo (navegador/SO), si está
// entre los soportados; si no, español.
function detectDeviceLang() {
  try {
    const cands =
      navigator.languages && navigator.languages.length
        ? navigator.languages
        : [navigator.language || navigator.userLanguage];
    for (const c of cands) {
      const code = String(c || "").toLowerCase();
      if (code.startsWith("es")) return "es";
      if (code.startsWith("en")) return "en";
      if (code.startsWith("zh")) return "zh"; // zh, zh-CN, zh-Hans, zh-TW…
      if (code.startsWith("pt")) return "pt";
      if (code.startsWith("fr")) return "fr"; // fr, fr-FR, fr-CA…
    }
  } catch {
    // sin navigator
  }
  return "es";
}

// Precedencia: preferencia guardada (el usuario eligió) > idioma del dispositivo.
function initialLang() {
  try {
    const saved = localStorage.getItem(LANG_STORAGE_KEY);
    if (saved && SUPPORTED.includes(saved)) return saved;
  } catch {
    // localStorage no disponible
  }
  return detectDeviceLang();
}

i18n.use(initReactI18next).init({
  resources: {
    es: { translation: es }, // base: la clave (español) ya es el texto
    en: { translation: en },
    zh: { translation: zh },
    pt: { translation: pt },
    fr: { translation: fr },
  },
  lng: initialLang(),
  fallbackLng: "es",
  // Claves en lenguaje natural → desactivar separadores por '.'/':' que
  // romperían cadenas con puntos o dos puntos.
  keySeparator: false,
  nsSeparator: false,
  returnEmptyString: false,
  interpolation: { escapeValue: false },
});

try {
  document.documentElement.lang = i18n.language;
} catch {
  // sin document (tests/SSR)
}

export function setLanguage(code) {
  i18n.changeLanguage(code);
  try {
    localStorage.setItem(LANG_STORAGE_KEY, code);
  } catch {
    // ignorar
  }
  try {
    document.documentElement.lang = code;
  } catch {
    // ignorar
  }
}

export default i18n;
